import { useState } from 'react';
import type { SearchFilters } from '../types';

interface SearchFiltersProps {
  onSearch: (filters: SearchFilters) => void;
}

export default function SearchFiltersComponent({ onSearch }: SearchFiltersProps) {
  const [searchType, setSearchType] = useState<SearchFilters['searchType']>('orderNo');
  const [searchValue, setSearchValue] = useState('');
  const [rechargeTypes, setRechargeTypes] = useState<string[]>([]);
  const [userTypes, setUserTypes] = useState<string[]>([]);
  const [rechargeMethods, setRechargeMethods] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const handleSearch = () => {
    onSearch({
      searchType,
      searchValue,
      rechargeTypes,
      userTypes,
      rechargeMethods,
      paymentMethods,
      startTime,
      endTime,
    });
  };

  const handleReset = () => {
    setSearchType('orderNo');
    setSearchValue('');
    setRechargeTypes([]);
    setUserTypes([]);
    setRechargeMethods([]);
    setPaymentMethods([]);
    setStartTime('');
    setEndTime('');
    onSearch({
      searchType: 'orderNo',
      searchValue: '',
      rechargeTypes: [],
      userTypes: [],
      rechargeMethods: [],
      paymentMethods: [],
      startTime: '',
      endTime: '',
    });
  };

  const toggleArrayValue = (arr: string[], value: string, setter: (arr: string[]) => void) => {
    if (arr.includes(value)) {
      setter(arr.filter((v) => v !== value));
    } else {
      setter([...arr, value]);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">搜索类型</label>
          <select
            value={searchType}
            onChange={(e) => setSearchType(e.target.value as SearchFilters['searchType'])}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            <option value="orderNo">充值订单号</option>
            <option value="userId">用户ID</option>
            <option value="userName">用户名称</option>
            <option value="operatorId">操作用户ID</option>
            <option value="operatorName">操作用户名称</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">搜索内容</label>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="请输入搜索内容"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">开始时间</label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">结束时间</label>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">充值类型</label>
          <div className="flex flex-wrap gap-2">
            {['充值', '红冲', '退款'].map((type) => (
              <label key={type} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rechargeTypes.includes(type)}
                  onChange={() => toggleArrayValue(rechargeTypes, type, setRechargeTypes)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 whitespace-nowrap">{type}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">用户类型</label>
          <div className="flex flex-wrap gap-2">
            {['个人', '企业', '学校'].map((type) => (
              <label key={type} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={userTypes.includes(type)}
                  onChange={() => toggleArrayValue(userTypes, type, setUserTypes)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 whitespace-nowrap">{type}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">充值方式</label>
          <div className="flex flex-wrap gap-2">
            {['后台充值', '用户充值'].map((method) => (
              <label key={method} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rechargeMethods.includes(method)}
                  onChange={() => toggleArrayValue(rechargeMethods, method, setRechargeMethods)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 whitespace-nowrap">{method}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">支付方式</label>
          <div className="flex flex-wrap gap-2">
            {['后台支付', '微信支付', '支付宝支付'].map((method) => (
              <label key={method} className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={paymentMethods.includes(method)}
                  onChange={() => toggleArrayValue(paymentMethods, method, setPaymentMethods)}
                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-700 whitespace-nowrap">{method}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-teal-600 text-white text-sm rounded-md hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-search-line mr-1"></i>
          搜索
        </button>
        <button
          onClick={handleReset}
          className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-refresh-line mr-1"></i>
          重置
        </button>
      </div>
    </div>
  );
}
